<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions">
          <div class="me-2 mb-2">
            <select v-model="filters.frequency" class="form-select">
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="daily">Daily</option>
            </select>
          </div>
          <div class="me-2 mb-2" v-if="filters.frequency === 'monthly'">
            <input v-model="filters.period" type="month" class="form-control" />
          </div>
          <div class="me-2 mb-2" v-else>
            <input v-model="filters.period_start" type="date" class="form-control" />
          </div>
          <div class="me-2 mb-2" v-if="filters.frequency !== 'monthly'">
            <input v-model="filters.period_end" type="date" class="form-control" />
          </div>
          <div class="me-2 mb-2">
            <select v-model="filters.status" class="form-select">
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <div class="me-2 mb-2">
            <button class="btn btn-primary" :disabled="loading" @click="fetchReport">
              <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
              Load
            </button>
          </div>
          <div class="me-2 mb-2">
            <button class="btn btn-success" :disabled="!selectedRowIds.length" @click="openBulkAcknowledge">
              Bulk Acknowledge Selected
            </button>
          </div>
          <div class="mb-2">
            <div class="dropdown">
              <a class="dropdown-toggle btn btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a class="dropdown-item rounded-1" href="javascript:void(0);" @click.prevent="exportReport('csv')">
                    <i class="ti ti-file-type-xls me-1"></i>Export as CSV
                  </a>
                </li>
                <li>
                  <a class="dropdown-item rounded-1" href="javascript:void(0);" @click.prevent="exportReport('pdf')">
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a class="dropdown-item rounded-1" href="javascript:void(0);" @click.prevent="exportAcknowledgment()">
                    <i class="ti ti-file-description me-1"></i>Acknowledgment PDF
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div v-if="summary" class="row dq-stat-grid">
        <div class="col-xl-3 col-md-6 d-flex mb-3" v-for="card in cards" :key="card.title">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between bg-light border rounded p-2 mb-2">
                <div>
                  <span class="fs-14 fw-normal text-truncate mb-1">{{ card.title }}</span>
                  <h5>{{ card.isMoney ? `${summary.currency} ${formatMoney(card.value)}` : card.value }}</h5>
                </div>
                <a href="javascript:void(0);" class="avatar avatar-md avatar-rounded" :class="`bg-transparent-${card.cls} border border-${card.cls}`">
                  <span :class="`text-${card.cls}`"><i :class="card.icon"></i></span>
                </a>
              </div>
              <p class="fs-12 fw-normal text-muted mb-0">Period: {{ summary.period_label || summary.period }}</p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="summary" class="row dq-stat-grid">
        <div class="col-xl-3 col-md-6 d-flex mb-3" v-for="card in operatorCards" :key="card.title">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between bg-light border rounded p-2 mb-2">
                <div>
                  <span class="fs-14 fw-normal text-truncate mb-1">{{ card.title }}</span>
                  <h5>{{ card.isMoney ? `${summary.currency} ${formatMoney(card.value)}` : card.value }}</h5>
                </div>
                <a href="javascript:void(0);" class="avatar avatar-md avatar-rounded" :class="`bg-transparent-${card.cls} border border-${card.cls}`">
                  <span :class="`text-${card.cls}`"><i :class="card.icon"></i></span>
                </a>
              </div>
              <p class="fs-12 fw-normal text-muted mb-0">{{ card.note }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="alert alert-info">
        Cash payouts can only be acknowledged after payroll is approved for the period.
        Rows marked <strong>Blocked</strong> will unlock once approval completes on Employee Salary.
        The acknowledgment PDF can be printed and filed as the signed cash register.
      </div>

      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">Cash Payroll Register</h5>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <a-table class="table datatable thead-light" :columns="columns" :data-source="rows" :loading="loading" :pagination="false" row-key="id" :row-selection="rowSelection">
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'amount'">
                  {{ record.currency }} {{ formatMoney(record.amount) }}
                </template>
                <template v-else-if="column.key === 'status'">
                  <span class="badge" :class="record.status === 'paid' ? 'badge-soft-success' : 'badge-soft-warning'">
                    {{ record.status === 'paid' ? 'Paid' : 'Pending' }}
                  </span>
                </template>
                <template v-else-if="column.key === 'approval_status'">
                  <span class="badge" :class="record.approval_status === 'approved' ? 'badge-soft-success' : 'badge-soft-danger'">
                    {{ record.approval_status === 'approved' ? 'Approved' : 'Blocked' }}
                  </span>
                </template>
                <template v-else-if="column.key === 'actions'">
                  <div class="d-flex gap-2 flex-wrap">
                    <button
                      v-if="record.status !== 'paid'"
                      class="btn btn-sm btn-success"
                      :disabled="record.approval_status !== 'approved' || actionLoadingId === record.id"
                      @click="openAcknowledge(record)"
                    >
                      Acknowledge & Mark Paid
                    </button>
                    <button
                      v-else
                      class="btn btn-sm btn-outline-warning"
                      :disabled="actionLoadingId === record.id"
                      @click="markPending(record)"
                    >
                      <span v-if="actionLoadingId === record.id" class="spinner-border spinner-border-sm me-1"></span>
                      Revert
                    </button>
                  </div>
                </template>
              </template>
            </a-table>
          </div>
        </div>
      </div>
    </div>

    <div v-if="ackModalOpen" class="dq-modal-backdrop" @click.self="closeAcknowledge">
      <div class="dq-modal-card">
        <div class="dq-modal-head">
          <div>
            <h5 class="mb-1">Acknowledge Cash Payout</h5>
            <p class="mb-0 text-muted">{{ selectedRow?.employee_name }} • {{ selectedRow?.employee_id }}</p>
          </div>
          <button class="btn btn-icon btn-sm btn-light" @click="closeAcknowledge">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <div class="dq-modal-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Receiver Name</label>
              <input v-model.trim="ackForm.receiver_name" type="text" class="form-control" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Receiver ID / Document No</label>
              <input v-model.trim="ackForm.receiver_document_no" type="text" class="form-control" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Payment Reference</label>
              <input v-model.trim="ackForm.payment_reference" type="text" class="form-control" placeholder="Optional voucher or ref" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Acknowledgment Signature</label>
              <input v-model.trim="ackForm.receiver_signature" type="text" class="form-control" placeholder="Type full name used as acknowledgement" />
            </div>
            <div class="col-12">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <label class="form-label mb-0">Draw Signature</label>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearSignatureCanvas">Clear</button>
              </div>
              <div class="dq-signature-pad">
                <canvas
                  ref="signatureCanvas"
                  class="dq-signature-canvas"
                  @mousedown="startSignature"
                  @mousemove="moveSignature"
                  @mouseup="endSignature"
                  @mouseleave="endSignature"
                  @touchstart.prevent="startSignature"
                  @touchmove.prevent="moveSignature"
                  @touchend.prevent="endSignature"
                ></canvas>
              </div>
              <small class="text-muted">Optional drawn signature. If captured, it will appear on the acknowledgment PDF.</small>
            </div>
            <div class="col-12">
              <label class="form-label">Notes</label>
              <textarea v-model.trim="ackForm.notes" rows="3" class="form-control"></textarea>
            </div>
          </div>
        </div>
        <div class="dq-modal-foot">
          <button class="btn btn-light me-2" @click="closeAcknowledge">Cancel</button>
          <button class="btn btn-success" :disabled="actionLoadingId === selectedRow?.id" @click="submitAcknowledge">
            <span v-if="actionLoadingId === selectedRow?.id" class="spinner-border spinner-border-sm me-1"></span>
            Confirm Payout
          </button>
        </div>
      </div>
    </div>

    <div v-if="bulkModalOpen" class="dq-modal-backdrop" @click.self="closeBulkAcknowledge">
      <div class="dq-modal-card dq-modal-card-wide">
        <div class="dq-modal-head">
          <div>
            <h5 class="mb-1">Bulk Cash Payout Acknowledgment</h5>
            <p class="mb-0 text-muted">{{ bulkForm.items.length }} selected employees</p>
          </div>
          <button class="btn btn-icon btn-sm btn-light" @click="closeBulkAcknowledge">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <div class="dq-modal-body">
          <div class="mb-3">
            <label class="form-label">Shared Payment Reference</label>
            <input v-model.trim="bulkForm.payment_reference" type="text" class="form-control" placeholder="Optional shared voucher or batch ref" />
          </div>
          <div class="table-responsive">
            <table class="table table-bordered align-middle">
              <thead>
                <tr>
                  <th>Emp ID</th>
                  <th>Name</th>
                  <th>Amount</th>
                  <th>Receiver Name</th>
                  <th>Document No</th>
                  <th>Signature</th>
                  <th>Drawn Sign</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in bulkForm.items" :key="item.id">
                  <td>{{ item.employee_id }}</td>
                  <td>{{ item.employee_name }}</td>
                  <td>{{ summary?.currency }} {{ formatMoney(item.amount) }}</td>
                  <td><input v-model.trim="item.receiver_name" type="text" class="form-control" /></td>
                  <td><input v-model.trim="item.receiver_document_no" type="text" class="form-control" /></td>
                  <td><input v-model.trim="item.receiver_signature" type="text" class="form-control" /></td>
                  <td>
                    <div class="d-flex align-items-center gap-2 flex-wrap">
                      <button type="button" class="btn btn-sm btn-outline-primary" @click="openBulkSignatureCapture(item)">
                        {{ item.receiver_signature_image ? "Update" : "Capture" }}
                      </button>
                      <span v-if="item.receiver_signature_image" class="badge badge-soft-success">Captured</span>
                    </div>
                  </td>
                  <td><input v-model.trim="item.notes" type="text" class="form-control" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="dq-modal-foot">
          <button class="btn btn-light me-2" @click="closeBulkAcknowledge">Cancel</button>
          <button class="btn btn-success" :disabled="bulkSubmitting" @click="submitBulkAcknowledge">
            <span v-if="bulkSubmitting" class="spinner-border spinner-border-sm me-1"></span>
            Confirm Selected Payouts
          </button>
        </div>
      </div>
    </div>

    <div v-if="bulkSignatureModalOpen" class="dq-modal-backdrop" @click.self="closeBulkSignatureCapture">
      <div class="dq-modal-card">
        <div class="dq-modal-head">
          <div>
            <h5 class="mb-1">Capture Receiver Signature</h5>
            <p class="mb-0 text-muted">{{ activeBulkItem?.employee_name }} • {{ activeBulkItem?.employee_id }}</p>
          </div>
          <button class="btn btn-icon btn-sm btn-light" @click="closeBulkSignatureCapture">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <div class="dq-modal-body">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <label class="form-label mb-0">Draw Signature</label>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearBulkSignatureCanvas">Clear</button>
          </div>
          <div class="dq-signature-pad">
            <canvas
              ref="bulkSignatureCanvas"
              class="dq-signature-canvas"
              @mousedown="startBulkSignature"
              @mousemove="moveBulkSignature"
              @mouseup="endBulkSignature"
              @mouseleave="endBulkSignature"
              @touchstart.prevent="startBulkSignature"
              @touchmove.prevent="moveBulkSignature"
              @touchend.prevent="endBulkSignature"
            ></canvas>
          </div>
          <small class="text-muted">The captured signature will be attached to this employee's cash acknowledgment record and PDF.</small>
        </div>
        <div class="dq-modal-foot">
          <button class="btn btn-light me-2" @click="closeBulkSignatureCapture">Cancel</button>
          <button class="btn btn-primary" @click="saveBulkSignatureCapture">Save Signature</button>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a></p>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref } from "vue";
import moment from "moment";
import reportsService from "@/services/reports.service";
import { toast } from "@/utils/toast";

const title = "Cash Payroll Report";
const text = "Administration";
const text1 = "Cash Payroll Report";

const loading = ref(false);
const actionLoadingId = ref(null);
const summary = ref(null);
const rows = ref([]);
const ackModalOpen = ref(false);
const bulkModalOpen = ref(false);
const selectedRow = ref(null);
const selectedRowIds = ref([]);
const bulkSubmitting = ref(false);
const signatureCanvas = ref(null);
const bulkSignatureCanvas = ref(null);
const signatureDrawing = ref(false);
const signatureDirty = ref(false);
const bulkSignatureModalOpen = ref(false);
const bulkSignatureDrawing = ref(false);
const bulkSignatureDirty = ref(false);
const activeBulkItem = ref(null);

const filters = reactive({
  frequency: "monthly",
  period: moment().format("YYYY-MM"),
  period_start: moment().startOf("week").format("YYYY-MM-DD"),
  period_end: moment().startOf("week").add(6, "days").format("YYYY-MM-DD"),
  status: "",
});

const ackForm = reactive({
  receiver_name: "",
  receiver_document_no: "",
  payment_reference: "",
  receiver_signature: "",
  receiver_signature_image: "",
  notes: "",
});

const bulkForm = reactive({
  payment_reference: "",
  items: [],
});

const columns = [
  { title: "Emp ID", dataIndex: "employee_id", key: "employee_id" },
  { title: "Name", dataIndex: "employee_name", key: "employee_name" },
  { title: "Department", dataIndex: "department_name", key: "department_name" },
  { title: "Designation", dataIndex: "designation_name", key: "designation_name" },
  { title: "Amount", dataIndex: "amount", key: "amount" },
  { title: "Status", dataIndex: "status", key: "status" },
  { title: "Approval", dataIndex: "approval_status", key: "approval_status" },
  { title: "Paid At", dataIndex: "paid_at", key: "paid_at" },
  { title: "Paid By", dataIndex: "paid_by_name", key: "paid_by_name" },
  { title: "Receiver", dataIndex: "receiver_name", key: "receiver_name" },
  { title: "Reference", dataIndex: "payment_reference", key: "payment_reference" },
  { title: "Actions", key: "actions" },
];

const rowSelection = computed(() => ({
  selectedRowKeys: selectedRowIds.value,
  onChange: (keys) => {
    selectedRowIds.value = keys;
  },
}));

const cards = computed(() => {
  if (!summary.value) return [];
  return [
    { title: "Employees", value: summary.value.employee_count || 0, cls: "primary", icon: "ti ti-users" },
    { title: "Total Cash", value: summary.value.total_amount || 0, cls: "info", icon: "ti ti-cash-banknote", isMoney: true },
    { title: "Paid", value: summary.value.paid_amount || 0, cls: "success", icon: "ti ti-checks", isMoney: true },
    { title: "Pending", value: summary.value.pending_amount || 0, cls: "warning", icon: "ti ti-alert-circle", isMoney: true },
  ];
});

const operatorCards = computed(() => {
  const readyRows = rows.value.filter((row) => row.status !== "paid" && row.approval_status === "approved");
  const blockedRows = rows.value.filter((row) => row.status !== "paid" && row.approval_status !== "approved");
  const selectedRows = rows.value.filter((row) => selectedRowIds.value.includes(row.id));

  return [
    {
      title: "Ready To Pay",
      value: readyRows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
      cls: "success",
      icon: "ti ti-cash",
      isMoney: true,
      note: `${readyRows.length} pending employees can be acknowledged now`,
    },
    {
      title: "Blocked",
      value: blockedRows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
      cls: "danger",
      icon: "ti ti-lock",
      isMoney: true,
      note: `${blockedRows.length} pending employees await payroll approval`,
    },
    {
      title: "Selected Employees",
      value: selectedRows.length,
      cls: "primary",
      icon: "ti ti-checklist",
      note: "Rows selected for bulk acknowledgment",
    },
    {
      title: "Selected Total",
      value: selectedRows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
      cls: "warning",
      icon: "ti ti-receipt-2",
      isMoney: true,
      note: "Total amount in the current row selection",
    },
  ];
});

function formatMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function resetAckForm() {
  ackForm.receiver_name = "";
  ackForm.receiver_document_no = "";
  ackForm.payment_reference = "";
  ackForm.receiver_signature = "";
  ackForm.receiver_signature_image = "";
  ackForm.notes = "";
}

function getSignatureCanvasContext() {
  const canvas = signatureCanvas.value;
  if (!canvas) return null;
  return canvas.getContext("2d");
}

function getBulkSignatureCanvasContext() {
  const canvas = bulkSignatureCanvas.value;
  if (!canvas) return null;
  return canvas.getContext("2d");
}

function setupCanvas(canvas) {
  if (!canvas) return;
  const ratio = window.devicePixelRatio || 1;
  const width = canvas.clientWidth || 520;
  const height = canvas.clientHeight || 160;
  canvas.width = Math.floor(width * ratio);
  canvas.height = Math.floor(height * ratio);
  const ctx = canvas.getContext("2d");
  ctx.scale(ratio, ratio);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.lineWidth = 2;
  ctx.strokeStyle = "#0f172a";
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
}

function resizeSignatureCanvas() {
  setupCanvas(signatureCanvas.value);
}

function resizeBulkSignatureCanvas() {
  setupCanvas(bulkSignatureCanvas.value);
}

function clearSignatureCanvas() {
  const canvas = signatureCanvas.value;
  const ctx = getSignatureCanvasContext();
  if (!canvas || !ctx) return;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.restore();
  resizeSignatureCanvas();
  signatureDirty.value = false;
  ackForm.receiver_signature_image = "";
}

function clearBulkSignatureCanvas() {
  const canvas = bulkSignatureCanvas.value;
  const ctx = getBulkSignatureCanvasContext();
  if (!canvas || !ctx) return;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.restore();
  resizeBulkSignatureCanvas();
  bulkSignatureDirty.value = false;
  if (activeBulkItem.value) {
    activeBulkItem.value.receiver_signature_image = "";
  }
}

function canvasPoint(event, canvas) {
  if (!canvas) return { x: 0, y: 0 };
  const rect = canvas.getBoundingClientRect();
  const source = event.touches?.[0] || event.changedTouches?.[0] || event;
  return {
    x: source.clientX - rect.left,
    y: source.clientY - rect.top,
  };
}

function startSignature(event) {
  const ctx = getSignatureCanvasContext();
  if (!ctx) return;
  signatureDrawing.value = true;
  signatureDirty.value = true;
  const { x, y } = canvasPoint(event, signatureCanvas.value);
  ctx.beginPath();
  ctx.moveTo(x, y);
}

function moveSignature(event) {
  if (!signatureDrawing.value) return;
  const ctx = getSignatureCanvasContext();
  if (!ctx) return;
  const { x, y } = canvasPoint(event, signatureCanvas.value);
  ctx.lineTo(x, y);
  ctx.stroke();
  event.preventDefault?.();
}

function endSignature() {
  if (!signatureDrawing.value) return;
  signatureDrawing.value = false;
  const canvas = signatureCanvas.value;
  if (canvas && signatureDirty.value) {
    ackForm.receiver_signature_image = canvas.toDataURL("image/png");
  }
}

function startBulkSignature(event) {
  const ctx = getBulkSignatureCanvasContext();
  if (!ctx) return;
  bulkSignatureDrawing.value = true;
  bulkSignatureDirty.value = true;
  const { x, y } = canvasPoint(event, bulkSignatureCanvas.value);
  ctx.beginPath();
  ctx.moveTo(x, y);
}

function moveBulkSignature(event) {
  if (!bulkSignatureDrawing.value) return;
  const ctx = getBulkSignatureCanvasContext();
  if (!ctx) return;
  const { x, y } = canvasPoint(event, bulkSignatureCanvas.value);
  ctx.lineTo(x, y);
  ctx.stroke();
  event.preventDefault?.();
}

function endBulkSignature() {
  if (!bulkSignatureDrawing.value) return;
  bulkSignatureDrawing.value = false;
  const canvas = bulkSignatureCanvas.value;
  if (canvas && bulkSignatureDirty.value && activeBulkItem.value) {
    activeBulkItem.value.receiver_signature_image = canvas.toDataURL("image/png");
  }
}

function resetBulkForm() {
  bulkForm.payment_reference = "";
  bulkForm.items = [];
}

async function openAcknowledge(record) {
  selectedRow.value = record;
  resetAckForm();
  ackForm.receiver_name = record.receiver_name || record.employee_name || "";
  ackForm.receiver_document_no = record.receiver_document_no || "";
  ackForm.payment_reference = record.payment_reference || "";
  ackForm.receiver_signature = record.receiver_signature || record.receiver_name || record.employee_name || "";
  ackForm.receiver_signature_image = record.receiver_signature_image || "";
  ackForm.notes = record.notes || "";
  ackModalOpen.value = true;
  await nextTick();
  resizeSignatureCanvas();
}

function closeAcknowledge() {
  ackModalOpen.value = false;
  selectedRow.value = null;
  resetAckForm();
}

function openBulkAcknowledge() {
  const selected = rows.value.filter((row) => selectedRowIds.value.includes(row.id) && row.status !== "paid");
  if (!selected.length) {
    toast("Select pending cash payout rows first.", "error");
    return;
  }

  resetBulkForm();
  bulkForm.items = selected.map((row) => ({
    id: row.id,
    employee_id: row.employee_id,
    employee_name: row.employee_name,
    amount: row.amount,
    receiver_name: row.receiver_name || row.employee_name || "",
    receiver_document_no: row.receiver_document_no || "",
    receiver_signature: row.receiver_signature || row.receiver_name || row.employee_name || "",
    receiver_signature_image: row.receiver_signature_image || "",
    notes: row.notes || "",
  }));
  bulkModalOpen.value = true;
}

function closeBulkAcknowledge() {
  bulkModalOpen.value = false;
  resetBulkForm();
}

async function openBulkSignatureCapture(item) {
  activeBulkItem.value = item;
  bulkSignatureModalOpen.value = true;
  await nextTick();
  resizeBulkSignatureCanvas();
}

function closeBulkSignatureCapture() {
  bulkSignatureModalOpen.value = false;
  activeBulkItem.value = null;
  bulkSignatureDrawing.value = false;
  bulkSignatureDirty.value = false;
}

function saveBulkSignatureCapture() {
  if (!activeBulkItem.value?.receiver_signature_image) {
    toast("Draw a signature before saving.", "error");
    return;
  }
  closeBulkSignatureCapture();
}

async function fetchReport() {
  loading.value = true;
  try {
    const params = buildParams();
    if (!params) {
      loading.value = false;
      return;
    }
    const { data } = await reportsService.cashPayroll({
      ...params,
      status: filters.status || undefined,
    });
    summary.value = data.summary || null;
    rows.value = data.rows || [];
    selectedRowIds.value = selectedRowIds.value.filter((id) => rows.value.some((row) => row.id === id));
  } catch (err) {
    toast(err?.response?.data?.message || "Failed to load cash payroll report.", "error");
  } finally {
    loading.value = false;
  }
}

async function exportReport(format) {
  try {
    const params = buildParams();
    if (!params) return;
    const res =
      format === "pdf"
        ? await reportsService.exportCashPayrollPDF({
            ...params,
            status: filters.status || undefined,
          })
        : await reportsService.exportCashPayrollCSV({
            ...params,
            status: filters.status || undefined,
          });

    downloadBlob(res.data, `cash-payroll-${reportPeriodToken()}.${format}`);
  } catch (err) {
    toast(err?.response?.data?.message || "Failed to export cash payroll report.", "error");
  }
}

async function exportAcknowledgment() {
  try {
    const params = buildParams();
    if (!params) return;
    const res = await reportsService.exportCashPayrollAcknowledgmentPDF({
      ...params,
      status: filters.status || undefined,
    });

    downloadBlob(res.data, `cash-payroll-acknowledgment-${reportPeriodToken()}.pdf`);
  } catch (err) {
    toast(err?.response?.data?.message || "Failed to export cash payroll acknowledgment.", "error");
  }
}

function buildParams() {
  if (filters.frequency === "monthly") {
    if (!filters.period) return null;
    return {
      frequency: "monthly",
      period: filters.period,
    };
  }

  if (!filters.period_start || !filters.period_end) return null;
  return {
    frequency: filters.frequency,
    period_start: filters.period_start,
    period_end: filters.period_end,
  };
}

function reportPeriodToken() {
  return filters.frequency === "monthly"
    ? filters.period
    : [filters.frequency, filters.period_start, filters.period_end].filter(Boolean).join("-");
}

async function submitAcknowledge() {
  if (!selectedRow.value) return;
  if (!ackForm.receiver_name || !ackForm.receiver_signature) {
    toast("Receiver name and acknowledgment signature are required.", "error");
    return;
  }

  actionLoadingId.value = selectedRow.value.id;
  try {
    await reportsService.markCashPayrollPaid(selectedRow.value.id, {
      receiver_name: ackForm.receiver_name,
      receiver_document_no: ackForm.receiver_document_no || undefined,
      payment_reference: ackForm.payment_reference || undefined,
      receiver_signature: ackForm.receiver_signature,
      receiver_signature_image: ackForm.receiver_signature_image || undefined,
      notes: ackForm.notes || undefined,
    });

    toast("Cash payout confirmed.");
    closeAcknowledge();
    await fetchReport();
  } catch (err) {
    toast(err?.response?.data?.message || "Failed to confirm cash payout.", "error");
  } finally {
    actionLoadingId.value = null;
  }
}

async function submitBulkAcknowledge() {
  if (!bulkForm.items.length) {
    toast("No selected cash payout rows to confirm.", "error");
    return;
  }

  const invalid = bulkForm.items.find((item) => !item.receiver_name || !item.receiver_signature);
  if (invalid) {
    toast(`Receiver name and signature are required for ${invalid.employee_id}.`, "error");
    return;
  }

  bulkSubmitting.value = true;
  try {
    await reportsService.bulkMarkCashPayrollPaid({
      payment_reference: bulkForm.payment_reference || undefined,
      items: bulkForm.items.map((item) => ({
        id: item.id,
        receiver_name: item.receiver_name,
        receiver_document_no: item.receiver_document_no || undefined,
        receiver_signature: item.receiver_signature,
        receiver_signature_image: item.receiver_signature_image || undefined,
        notes: item.notes || undefined,
      })),
    });

    toast("Selected cash payouts confirmed.");
    closeBulkAcknowledge();
    await fetchReport();
  } catch (err) {
    toast(err?.response?.data?.message || "Failed to confirm selected cash payouts.", "error");
  } finally {
    bulkSubmitting.value = false;
  }
}

async function markPending(record) {
  actionLoadingId.value = record.id;
  try {
    await reportsService.markCashPayrollPending(record.id);
    toast("Cash payout reverted.");
    await fetchReport();
  } catch (err) {
    toast(err?.response?.data?.message || "Failed to revert cash payout.", "error");
  } finally {
    actionLoadingId.value = null;
  }
}

onMounted(fetchReport);
</script>

<style scoped>
.dq-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
}

.dq-modal-card {
  width: min(720px, 100%);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.18);
  overflow: hidden;
}

.dq-modal-head,
.dq-modal-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.dq-modal-foot {
  border-bottom: 0;
  border-top: 1px solid #e5e7eb;
  justify-content: flex-end;
}

.dq-modal-body {
  padding: 1.25rem;
}

.dq-modal-card-wide {
  width: min(1200px, 100%);
}

.dq-stat-grid + .dq-stat-grid {
  margin-top: -0.5rem;
}

.dq-signature-pad {
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  padding: 0.25rem;
}

.dq-signature-canvas {
  width: 100%;
  height: 160px;
  border-radius: 10px;
  touch-action: none;
  cursor: crosshair;
  display: block;
}
</style>


